import React, { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import SVGIcon from '../../../assets/images/svg/SVGIcon';
import { ROUTES } from '../../../routes/urls';
import {
  Div,
  Icon,
  IconDiv,
  IconWrapper,
  LHS,
  Line,
  LogoutMenu,
  Menu,
  MenuSectionDiv,
  NavbarMain,
  ProfileMenu,
  RHS,
  Submenu
} from './styles';
import Modal from '../../modals/modal';
import ConfirmationModal from '../../modals/confirmationModal';
import { trackAnalyticActivity } from '../../../services/analyticsService';
import { saveProjectTemplateDetails, updateNotificationSettingDetails } from '../../../services/settingServices';
import { PROJECT_ANALYTICS } from '../../../global/analyticsConstants';
import { useMobileDevice } from '../../../global/useMobile';
import { Dropdown, DropdownItem } from '../../dropdown';
import { menuDisplayItems, settingsMenuData } from '../../../global/row';
import HelpSupportDropdown from '../helpSupportDropdown';
import { logout } from '../../../services/authServices';
import { COLOR_THEME_TEXT, ICON_SIZE } from '../../../global/constants';
import { useThemeSwitcher } from './hooks/useThemeSwitcher';
import { useSettingNavbarListeners } from './hooks/useSettingNavbarListeners';
import ResponsiveHeader from '../../responsiveHeader';
import LayoutWithoutNavbar from '../../layoutWithoutNavbar';
import { rootStore } from '../../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import { convertProjectTemplateModelToInterface } from '../../../mobx/common';
import MenuSection from '../menuSection';
import NavbarActionButton from '../../navbarActionButton';
import PlanUpgradeModel from '../../modals/planUpgradeModal';
import PlanUpgradeModelStep1 from '../../modals/planUpgradeModalStep1';
import { BackButtonMainDiv } from '../reportNavbar/styles';

type ModalState =
  | { type: 'none' }
  | { type: 'planUpgrade' }
  | { type: 'planUpgradeStep' }
  | { type: 'alert' }
  | { type: 'contactUs' };

const SettingNavbar: React.FC = () => {
  //State
  const [modal, setModal] = useState<ModalState>({ type: 'none' });
  const [navigatePath, setNavigatePath] = useState('');
  const [updatedDataName, setUpdatedDataName] = useState('');
  //Mobx store variable
  const { workspaceStore, projectTemplateStore, settingStore } = rootStore;
  const { currentWorkspace: workspace } = workspaceStore;
  const { currentProjectTemplate } = projectTemplateStore;
  const { loading: settingLoading, notificationSettingDetails } = settingStore;
  //Other variable
  const isMobile = useMobileDevice();
  const history = useHistory();

  // Custom hooks
  const { themeType, onThemeChange } = useThemeSwitcher();
  useSettingNavbarListeners({
    setAlertModalOpen: () => setModal({ type: 'alert' }),
    setNavigatePath,
    setUpdatedDataName
  });

  const onClickNavbarItem = useCallback(
    (path: string) => {
      history.push(path);
    },
    [history]
  );

  const closeAlertModal = useCallback(() => {
    if (updatedDataName === 'projectTemplateDetails') {
      currentProjectTemplate?.setUpdated(false);
    } else if (updatedDataName === 'notificationSettingDetails') {
      settingStore.setNotificationProperty('updated', false);
    }
    setModal({ type: 'none' });
    if (navigatePath) {
      history.push(navigatePath);
    }
  }, [currentProjectTemplate, history, navigatePath, settingStore, updatedDataName]);

  const onClickSave = useCallback(async () => {
    if (updatedDataName === 'projectTemplateDetails') {
      const cloneDetails = convertProjectTemplateModelToInterface(currentProjectTemplate!);
      trackAnalyticActivity(PROJECT_ANALYTICS.TEMPLATE_DATA_UPDATED);
      const result = await saveProjectTemplateDetails(cloneDetails);
      currentProjectTemplate?.setUpdated(false);
      if (result) closeAlertModal();
    } else if (updatedDataName === 'notificationSettingDetails') {
      const cloneDetails = JSON.parse(JSON.stringify(notificationSettingDetails));
      delete cloneDetails?.updated;
      const result = await updateNotificationSettingDetails(cloneDetails);
      if (result) closeAlertModal();
    }
  }, [closeAlertModal, notificationSettingDetails, currentProjectTemplate, updatedDataName]);

  const onHelpSupport = useCallback(() => {
    setModal(modal.type === 'contactUs' ? { type: 'none' } : { type: 'contactUs' });
  }, [modal.type]);

  const handleClickOpenPlanUpgradeModelStep = useCallback(() => {
    setModal({ type: 'planUpgradeStep' });
  }, []);

  const handleClickClosePlanUpgradeModelStep = useCallback(() => {
    setModal({ type: 'none' });
  }, []);

  const clickCloseModel = useCallback(() => {
    setModal({ type: 'none' });
  }, []);

  const onClickOpenModel = useCallback(() => {
    setModal({ type: 'planUpgrade' });
  }, []);

  const onClickLogout = useCallback(async () => {
    await logout();
    history.push(ROUTES.LOGIN);
    window.location.reload();
  }, [history]);

  const renderNavbarList = useCallback(() => {
    const filteredMenuData = settingsMenuData.filter((item) => {
      if (item.title === 'Workspace') {
        return workspace?.id && workspace?.isAdmin;
      }
      return true;
    });
    return filteredMenuData.map((item) => {
      const updatedItems =
        item.title === 'You' && isMobile
          ? [
              ...item.items,
              {
                customComponent: (
                  <React.Fragment key='you-with-help'>
                    <Submenu role='button' onClick={onHelpSupport}>
                      <HelpSupportDropdown
                        setOpenContactUsModal={() =>
                          setModal(modal.type === 'contactUs' ? { type: 'none' } : { type: 'contactUs' })
                        }
                        isMobile={isMobile}
                        isSetting={true}
                        onClickOpenModel={onClickOpenModel}
                      />
                    </Submenu>
                  </React.Fragment>
                )
              }
            ]
          : item.items;
      return (
        <MenuSection
          key={item.title}
          title={isMobile ? undefined : item.title}
          items={updatedItems}
          showDivider={isMobile}
        />
      );
    });
  }, [isMobile, onClickOpenModel, onHelpSupport, modal.type, workspace?.id, workspace?.isAdmin]);

  return (
    <>
      <LayoutWithoutNavbar>
        <NavbarMain className='setting-navbar'>
          {/* This div added for click when changed theme */}
          <div className='blank-div-click' />
          {!isMobile ? (
            <BackButtonMainDiv>
              <NavbarActionButton
                className='desktop-profile'
                text={'Back'}
                icon={
                  <SVGIcon
                    name='new-back-arrow-icon'
                    width={ICON_SIZE.MD}
                    height={ICON_SIZE.MD}
                    viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                  />
                }
                onClick={() => onClickNavbarItem(ROUTES.INBOX)}
                isPadding={true}
              />
            </BackButtonMainDiv>
          ) : (
            <ResponsiveHeader title='Settings' noMargin={true} />
          )}
          <MenuSectionDiv>
            {isMobile && (
              <>
                <Menu>
                  <Div>
                    <ProfileMenu>
                      <IconWrapper>
                        <Icon>
                          <SVGIcon
                            name='organization-icon'
                            width={ICON_SIZE.LG}
                            height={ICON_SIZE.LG}
                            viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                            className='svg-icon'
                          />
                        </Icon>
                        <LHS>
                          <p>Switch Organisation</p>
                        </LHS>
                      </IconWrapper>
                      <RHS
                        role='button'
                        onClick={() => {
                          history.push(ROUTES.SELECT_WORKSPACE);
                        }}>
                        <p>{workspace?.name}</p>
                        <IconDiv>
                          <SVGIcon
                            name='updated-right-arrow-icon'
                            width={ICON_SIZE.XL}
                            height={ICON_SIZE.XL}
                            viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                            fill='var(--text-secondary)'
                          />
                        </IconDiv>
                      </RHS>
                    </ProfileMenu>
                    <ProfileMenu>
                      <IconWrapper>
                        <Icon>
                          <SVGIcon
                            name='display-icon'
                            width={ICON_SIZE.LG}
                            height={ICON_SIZE.LG}
                            viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                            className='svg-icon'
                          />
                        </Icon>
                        <LHS>
                          <p>Display</p>
                        </LHS>
                      </IconWrapper>
                      <RHS>
                        <Dropdown
                          trigger='click'
                          content={
                            <RHS>
                              <p>{COLOR_THEME_TEXT[themeType]}</p>
                              <IconDiv>
                                <SVGIcon
                                  name='down-arrow-icon'
                                  width={ICON_SIZE.XL}
                                  height={ICON_SIZE.XL}
                                  viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                                  fill='var(--text-secondary)'
                                />
                              </IconDiv>
                            </RHS>
                          }
                          modalTitle='Display'>
                          {menuDisplayItems.map((item, index) => (
                            <DropdownItem
                              key={index}
                              label={item.label}
                              isSelected={item.label === COLOR_THEME_TEXT[themeType]}
                              onClick={() => onThemeChange(item.label.toLowerCase())}
                            />
                          ))}
                        </Dropdown>
                      </RHS>
                    </ProfileMenu>
                  </Div>
                </Menu>
                <Line />
              </>
            )}
            {renderNavbarList()}
            {isMobile && (
              <>
                <Menu>
                  <Div className='bottom-div'>
                    <LogoutMenu role='button' onClick={onClickLogout}>
                      <Icon>
                        <SVGIcon
                          name='exit-icon'
                          width={ICON_SIZE.XL}
                          height={ICON_SIZE.XL}
                          viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                          fill='var(--accent-error)'
                        />
                      </Icon>
                      <LHS className='log-out'>
                        <p>Log out</p>
                      </LHS>
                    </LogoutMenu>
                  </Div>
                </Menu>
              </>
            )}
          </MenuSectionDiv>
        </NavbarMain>

        {modal.type === 'alert' && (
          <Modal open={true} onClose={closeAlertModal} maxWidth={'336px'}>
            <ConfirmationModal
              loading={settingLoading}
              onOk={onClickSave}
              onClose={closeAlertModal}
              modalTitle='Save new changes?'
              description='Do you want to save new changes?'
            />
          </Modal>
        )}
        {modal.type === 'planUpgrade' && (
          <Modal open={true} maxWidth='692px' onClose={clickCloseModel} isResponsiveMaxWidth={true}>
            <PlanUpgradeModel onClickUpgrade={handleClickOpenPlanUpgradeModelStep} closeModel={clickCloseModel} />
          </Modal>
        )}
        {modal.type === 'planUpgradeStep' && (
          <Modal
            open={true}
            maxWidth='692px'
            onClose={handleClickClosePlanUpgradeModelStep}
            isResponsiveMaxWidth={true}>
            <PlanUpgradeModelStep1 />
          </Modal>
        )}
      </LayoutWithoutNavbar>
    </>
  );
};

export default observer(SettingNavbar);
